/**
 * AccountTypeItem Component
 *
 * A reusable UI element that represents a selectable account type option
 * (e.g., patient, doctor, admin). Displays an image, title, description,
 * and optional error message. Styles dynamically adjust based on active state.
 *
 * Features:
 * - Renders account type with an image, title, and description.
 * - Highlights active selection with border, background, and bold text.
 * - Switches image between default and active image when selected.
 * - Shows an error message if provided.
 *
 * Props (AccountTypeItemProps):
 * @param {string} imgUrl - Default image URL.
 * @param {string} activeImg - Image URL to display when the item is active.
 * @param {string} title - Account type title (e.g., "Doctor").
 * @param {string} desc - Account type description.
 * @param {boolean} active - Whether the account type is currently selected.
 * @param {string} [error] - Optional error message to display below the item.
 *
 * Example Usage:
 * <AccountTypeItem
 *   imgUrl="/images/patient.png"
 *   activeImg="/images/patient-active.png"
 *   title="Patient"
 *   desc="Access your health records and appointments."
 *   active={true}
 *   error="Please select an account type"
 * />
 */

import React from "react";
import { AccountTypeItemProps } from "../../types/GeneralComponents";
const AccountTypeItem: React.FC<AccountTypeItemProps> = ({
  imgUrl,
  title,
  desc,
  activeImg,
  active,
  error,
}) => {
  return (
    <article
      className={`flex cursor-pointer items-center gap-4 p-4 ${
        active ? "rounded-md border border-primaryMain bg-primaryLighter" : ""
      }`}
    >
      <img
        src={active ? activeImg : imgUrl}
        alt="type-img"
        loading="lazy"
        className={`h-14 w-14 rounded-xl object-cover ${
          active
            ? "rounded-xl bg-primaryMain p-2"
            : "rounded-xl border border-primaryMain p-0"
        }`}
      />
      <div className="flex flex-col gap-1">
        <h1
          className={`text-sm ${
            active ? "font-bold text-headingTextColor" : "text-headingTextColor"
          } `}
        >
          {title}
        </h1>
        <p
          className={`text-xs ${
            active ? "text-headingTextColor" : "text-textColor"
          } `}
        >
          {desc}
        </p>
      </div>
      <p className="text-errorMain">{error}</p>
    </article>
  );
};

export default AccountTypeItem;
